import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { listTickets, pollMail, TicketSummary } from '../lib/api';
import { signOut } from '../lib/auth';
import { formatMailDate, mailDateMs } from '../lib/dates';
import { Splitter, usePersistedSize } from '../lib/resize';
import { ProgressBanner } from './ProgressBanner';
import { TicketWorkspace } from './TicketWorkspace';

type Props = { onLogout: () => void };

type SortKey = 'mail_date' | 'updated_at' | 'subject' | 'from' | 'rischio' | 'status';

const RISK_RANK: Record<string, number> = { alto: 3, medio: 2, basso: 1 };
const MOBILE_MQ = '(max-width: 768px)';

function sortTickets(items: TicketSummary[], key: SortKey, dir: 'asc' | 'desc') {
  const mul = dir === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    let cmp = 0;
    switch (key) {
      case 'mail_date':
        cmp = mailDateMs(a.mail_date) - mailDateMs(b.mail_date);
        if (!cmp) cmp = (a.created_at || 0) - (b.created_at || 0);
        break;
      case 'updated_at':
        cmp = (a.updated_at || 0) - (b.updated_at || 0);
        break;
      case 'rischio':
        cmp = (RISK_RANK[a.rischio || ''] || 0) - (RISK_RANK[b.rischio || ''] || 0);
        break;
      case 'subject':
        cmp = (a.subject || '').localeCompare(b.subject || '', 'it', { sensitivity: 'base' });
        break;
      case 'from':
        cmp = (a.from || '').localeCompare(b.from || '', 'it', { sensitivity: 'base' });
        break;
      case 'status':
        cmp = (a.status || '').localeCompare(b.status || '', 'it');
        break;
      default:
        cmp = 0;
    }
    return cmp * mul;
  });
}

function matchesQuery(t: TicketSummary, q: string) {
  if (!q.trim()) return true;
  const hay = [t.subject, t.from, t.product, t.status, t.rischio, t.ticket_id]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((token) => hay.includes(token));
}

function useIsMobile() {
  const [mobile, setMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_MQ).matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const onChange = () => setMobile(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return mobile;
}

export function ConsoleApp({ onLogout }: Props) {
  const [tickets, setTickets] = useState<TicketSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const [pollMsg, setPollMsg] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>(() => {
    return (localStorage.getItem('supporto_sort_key') as SortKey) || 'mail_date';
  });
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(() => {
    return (localStorage.getItem('supporto_sort_dir') as 'asc' | 'desc') || 'desc';
  });
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { size: sidebarW, startDrag } = usePersistedSize('supporto_sidebar_w', 320, 240, 560);

  const refresh = useCallback(async () => {
    try {
      const res = await listTickets();
      setTickets(res.tickets || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  const forcePoll = useCallback(async () => {
    if (polling) return;
    setPolling(true);
    setPollMsg(null);
    setError(null);
    try {
      const res = await pollMail();
      setPollMsg(res.message || 'Controllo casella avviato…');
      await refresh();
      const delays = [3000, 6000, 12000, 20000];
      for (const delay of delays) {
        await new Promise((r) => setTimeout(r, delay));
        await refresh();
      }
      setPollMsg('Lista aggiornata. Se manca ancora qualcosa, riprova tra un minuto.');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPollMsg(null);
    } finally {
      setPolling(false);
    }
  }, [polling, refresh]);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [refresh]);

  useEffect(() => {
    localStorage.setItem('supporto_sort_key', sortKey);
    localStorage.setItem('supporto_sort_dir', sortDir);
  }, [sortKey, sortDir]);

  const visible = useMemo(() => {
    const filtered = tickets.filter((t) => matchesQuery(t, query));
    return sortTickets(filtered, sortKey, sortDir);
  }, [tickets, query, sortKey, sortDir]);

  const showList = !isMobile || !ticketId;
  const showDetail = !isMobile || Boolean(ticketId);

  return (
    <div
      className={`app-shell${isMobile ? ' is-mobile' : ''}${ticketId ? ' has-ticket' : ''}`}
      style={isMobile ? undefined : { gridTemplateColumns: `${sidebarW}px 6px 1fr` }}
    >
      <aside className="sidebar" hidden={!showList} aria-hidden={!showList}>
        <div className="brand">
          <h1>Supporto Q&amp;S</h1>
          <p>
            {visible.length} di {tickets.length} ticket
          </p>
          <button
            type="button"
            className="btn primary poll-btn"
            disabled={polling}
            onClick={() => void forcePoll()}
            title="Forza il controllo IMAP della casella supporto@"
          >
            {polling ? (
              <>
                <span className="spinner spinner-sm spinner-on-dark" aria-hidden />
                Controllo in corso…
              </>
            ) : (
              'Controlla email ora'
            )}
          </button>
          {pollMsg && (
            <p className={`poll-msg${polling ? ' poll-msg-busy' : ''}`}>
              {polling && <span className="spinner spinner-sm" aria-hidden />}
              {pollMsg}
            </p>
          )}
          {polling && (
            <ProgressBanner
              label="Controllo casella IMAP…"
              detail="Le nuove email compariranno nella lista man mano"
            />
          )}
        </div>

        <div className="list-controls">
          <input
            className="search-input"
            type="search"
            placeholder="Cerca oggetto, mittente, prodotto…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Cerca ticket"
          />
          <div className="sort-row">
            <label htmlFor="sort-key">Ordina</label>
            <select
              id="sort-key"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
            >
              <option value="mail_date">Data arrivo</option>
              <option value="updated_at">Ultimo aggiornamento</option>
              <option value="subject">Oggetto</option>
              <option value="from">Mittente</option>
              <option value="rischio">Rischio</option>
              <option value="status">Stato</option>
            </select>
            <button
              type="button"
              className="btn sort-dir"
              title={sortDir === 'desc' ? 'Decrescente' : 'Crescente'}
              onClick={() => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}
            >
              {sortDir === 'desc' ? '↓' : '↑'}
            </button>
          </div>
        </div>

        <div className="ticket-list">
          {error && (
            <div className="error" style={{ padding: 8 }}>
              {error}
            </div>
          )}
          {!visible.length && !error && (
            <div className="empty" style={{ fontSize: '0.9rem' }}>
              {tickets.length ? 'Nessun risultato' : 'Nessun ticket'}
            </div>
          )}
          {visible.map((t) => (
            <button
              key={t.ticket_id}
              className={`ticket-item ${ticketId === t.ticket_id ? 'active' : ''}`}
              onClick={() => navigate(`/tickets/${t.ticket_id}`)}
            >
              <div className="subj">{t.subject || '(senza oggetto)'}</div>
              <div className="from-line">{t.from || '—'}</div>
              <div className="meta">
                <span className="when" title="Data email">
                  {formatMailDate(t.mail_date || t.created_at)}
                </span>
                <span>{t.product || 'n/d'}</span>
                <span>{t.status}</span>
                {t.rischio && <span className={`badge ${t.rischio}`}>{t.rischio}</span>}
              </div>
            </button>
          ))}
        </div>
        <div className="sidebar-footer">
          <button
            className="btn"
            style={{ width: '100%' }}
            onClick={() => {
              signOut();
              onLogout();
            }}
          >
            Esci
          </button>
        </div>
      </aside>

      {!isMobile && <Splitter orientation="vertical" onMouseDown={startDrag('x')} />}

      <main className="main" hidden={!showDetail} aria-hidden={!showDetail}>
        {ticketId ? (
          <TicketWorkspace
            ticketId={ticketId}
            onUpdated={refresh}
            onBack={isMobile ? () => navigate('/') : undefined}
          />
        ) : (
          <div className="empty desktop-only-empty">Seleziona un ticket dalla lista</div>
        )}
      </main>
    </div>
  );
}
